import { useState, useEffect } from "react";

interface UserProfile {
  id: string;
  email: string;
  email_verified: boolean;
  credits: number;
  created_at: string;
  full_name?: string;
  avatar_url?: string;
}

interface ApiCall {
  id: number;
  endpoint: string;
  status: string;
  response_time_ms: number;
  credits_consumed: number;
  error_message?: string;
  called_at: string;
}

interface Pagination {
  limit: number;
  offset: number;
  total: number;
  has_more: boolean;
}

export const Account = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [apiCalls, setApiCalls] = useState<ApiCall[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [customAmount, setCustomAmount] = useState<string>("");
  const [purchasingCredits, setPurchasingCredits] = useState(false);
  const [showPricing, setShowPricing] = useState(false);

  useEffect(() => {
    const checkAuthAndLoadData = async () => {
      try {
        const authResponse = await fetch("/api/auth/validate-session");
        if (!authResponse.ok) {
          window.location.href = "/api/auth/signin";
          return;
        }
        await Promise.all([loadProfile(), loadApiCalls(0)]);
      } catch (error) {
        console.error("Error loading data:", error);
        window.location.href = "/api/auth/signin";
      } finally {
        setLoading(false);
      }
    };

    // Handle success/cancel from Stripe Checkout first
    const urlParams = new URLSearchParams(window.location.search);
    const isSuccess = urlParams.get("success") === "true";
    const isCanceled = urlParams.get("canceled") === "true";

    if (isSuccess) {
      setMessage({
        type: "success",
        text: "Payment successful! Your credits have been added.",
      });
      // Clean up URL
      window.history.replaceState({}, "", "/account");
      setTimeout(() => setMessage(null), 8000);

      // Reload profile after a short delay to allow webhook to process
      setTimeout(() => {
        loadProfile();
      }, 2000);
    } else if (isCanceled) {
      setMessage({
        type: "error",
        text: "Payment was canceled. No charges were made.",
      });
      // Clean up URL
      window.history.replaceState({}, "", "/account");
      setTimeout(() => setMessage(null), 5000);
    }

    checkAuthAndLoadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProfile = async () => {
    try {
      const response = await fetch("/api/user/profile");
      if (response.ok) {
        const data = await response.json();
        setProfile(data.profile);
      } else {
        const data = await response.json();
        throw new Error(data.message || "Failed to load profile");
      }
    } catch (error: any) {
      console.error("Error loading profile:", error);
      setError(error.message || "Failed to load profile data");
    }
  };

  const loadApiCalls = async (offset: number) => {
    try {
      const response = await fetch(
        `/api/user/api-calls?limit=20&offset=${offset}`
      );
      if (response.ok) {
        const data = await response.json();
        if (offset === 0) {
          setApiCalls(data.calls);
        } else {
          setApiCalls((prev) => [...prev, ...data.calls]);
        }
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error("Error loading API calls:", error);
    }
  };

  const handleLoadMore = async () => {
    if (!pagination || loadingMore) return;
    setLoadingMore(true);
    await loadApiCalls(pagination.offset + pagination.limit);
    setLoadingMore(false);
  };

  const handleSignOut = async () => {
    try {
      await fetch("/api/auth/signout", { method: "POST" });
      window.location.href = "/";
    } catch (error) {
      console.error("Error signing out:", error);
      window.location.href = "/";
    }
  };

  const handleResendVerification = async () => {
    if (!profile) return;
    try {
      const response = await fetch("/api/auth/resend-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: profile.email }),
      });
      const data = await response.json();
      setMessage({
        type: response.ok ? "success" : "error",
        text: data.message || "Verification email sent!",
      });
      setTimeout(() => setMessage(null), 5000);
    } catch (error) {
      setMessage({ type: "error", text: "Failed to send verification email" });
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const handleResetPassword = async () => {
    if (!profile) return;
    try {
      const response = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: profile.email }),
      });
      const data = await response.json();
      setMessage({
        type: response.ok ? "success" : "error",
        text: data.message || "Password reset email sent!",
      });
      setTimeout(() => setMessage(null), 5000);
    } catch (error) {
      setMessage({
        type: "error",
        text: "Failed to send password reset email",
      });
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const handlePurchaseCredits = async (amount: number) => {
    if (purchasingCredits) return;
    setPurchasingCredits(true);

    try {
      const response = await fetch("/api/stripe/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount }),
      });

      const data = await response.json();

      if (response.ok && data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        setMessage({
          type: "error",
          text: data.message || "Failed to create checkout session",
        });
        setPurchasingCredits(false);
        setTimeout(() => setMessage(null), 5000);
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: "Failed to initiate purchase",
      });
      setPurchasingCredits(false);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const handleCustomAmountPurchase = () => {
    const amount = parseInt(customAmount);
    if (isNaN(amount) || amount < 1 || amount > 1000) {
      setMessage({
        type: "error",
        text: "Please enter a valid amount between $1 and $1000",
      });
      setTimeout(() => setMessage(null), 5000);
      return;
    }
    handlePurchaseCredits(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div
        id="account"
        style={{
          minHeight: "100vh",
          background: "#001765",
          padding: "100px 20px",
        }}
      >
        <div
          style={{ textAlign: "center", color: "#fdfae5", fontSize: "24px" }}
        >
          Loading...
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div
        id="account"
        style={{
          minHeight: "100vh",
          background: "#001765",
          padding: "100px 20px",
        }}
      >
        <div
          style={{ maxWidth: "600px", margin: "0 auto", textAlign: "center" }}
        >
          <h2 style={{ color: "#fdfae5", marginBottom: "20px" }}>
            Error Loading Account
          </h2>
          <p
            style={{ color: "rgba(253, 250, 229, 0.8)", marginBottom: "30px" }}
          >
            {error || "Unable to load profile"}
          </p>
          <button
            onClick={async () => {
              try {
                await fetch("/api/auth/signout", { method: "POST" });
              } catch (e) {
                // Ignore errors
              }
              window.location.href = "/api/auth/signin";
            }}
            style={{
              padding: "12px 30px",
              background: "#fdfae5",
              color: "#001765",
              border: "none",
              borderRadius: "6px",
              fontSize: "16px",
              fontWeight: 600,
              cursor: "pointer",
              marginRight: "10px",
            }}
          >
            Sign In Again
          </button>
          <button
            onClick={() => (window.location.href = "/")}
            style={{
              padding: "12px 30px",
              background: "transparent",
              color: "#fdfae5",
              border: "2px solid #fdfae5",
              borderRadius: "6px",
              fontSize: "16px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      id="account"
      style={{
        minHeight: "100vh",
        background: "#001765",
        padding: "100px 20px 60px",
      }}
    >
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        {/* Message Alert */}
        {message && (
          <div
            style={{
              background: message.type === "success" ? "#28a745" : "#dc3545",
              color: "white",
              padding: "16px 24px",
              borderRadius: "8px",
              marginBottom: "30px",
              fontSize: "15px",
              fontWeight: 500,
              border: "2px solid rgba(255,255,255,0.3)",
            }}
          >
            {message.text}
          </div>
        )}

        {/* Profile Header */}
        <div
          style={{
            background: "rgba(0, 29, 129, 0.5)",
            border: "2px solid rgba(253, 250, 229, 0.2)",
            borderRadius: "12px",
            padding: "40px",
            marginBottom: "30px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "30px",
            }}
          >
            {/* User Info */}
            <div style={{ flex: "1", minWidth: "300px" }}>
              <h1
                style={{
                  fontSize: "32px",
                  fontWeight: 700,
                  color: "#fdfae5",
                  marginBottom: "8px",
                }}
              >
                {profile.full_name || "Account"}
              </h1>
              <div
                style={{
                  fontSize: "16px",
                  color: "rgba(253, 250, 229, 0.8)",
                  marginBottom: "4px",
                }}
              >
                {profile.email}
                {profile.email_verified && (
                  <span
                    style={{
                      marginLeft: "8px",
                      color: "#28a745",
                      fontSize: "14px",
                      fontWeight: 600,
                    }}
                  >
                    ✓ Verified
                  </span>
                )}
              </div>
              <div
                style={{ fontSize: "14px", color: "rgba(253, 250, 229, 0.6)" }}
              >
                Member since {formatDate(profile.created_at)}
              </div>
            </div>

            {/* Credits Display */}
            <div
              style={{
                background: "rgba(253, 250, 229, 0.1)",
                padding: "24px 40px",
                borderRadius: "12px",
                textAlign: "center",
                minWidth: "200px",
                border: "2px solid rgba(253, 250, 229, 0.3)",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  color: "rgba(253, 250, 229, 0.8)",
                  fontWeight: 600,
                  marginBottom: "8px",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                }}
              >
                Balance
              </div>
              <div
                style={{
                  fontSize: "42px",
                  color: "#fdfae5",
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                ${profile.credits.toFixed(2)}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div
            style={{
              marginTop: "30px",
              paddingTop: "30px",
              borderTop: "1px solid rgba(253, 250, 229, 0.2)",
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            {!profile.email_verified && (
              <button
                onClick={handleResendVerification}
                style={{
                  padding: "12px 24px",
                  background: "#28a745",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "15px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Resend Verification
              </button>
            )}
            <button
              onClick={handleResetPassword}
              style={{
                padding: "12px 24px",
                background: "#fdfae5",
                color: "#001765",
                border: "none",
                borderRadius: "6px",
                fontSize: "15px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Reset Password
            </button>
            <button
              onClick={() => (window.location.href = "/")}
              style={{
                padding: "12px 24px",
                background: "transparent",
                color: "#fdfae5",
                border: "2px solid rgba(253, 250, 229, 0.3)",
                borderRadius: "6px",
                fontSize: "15px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Back to Home
            </button>
            <button
              onClick={handleSignOut}
              style={{
                padding: "12px 24px",
                background: "transparent",
                color: "#dc3545",
                border: "2px solid #dc3545",
                borderRadius: "6px",
                fontSize: "15px",
                fontWeight: 600,
                cursor: "pointer",
                marginLeft: "auto",
              }}
            >
              Sign Out
            </button>
          </div>
        </div>

        {/* Buy Credits Section */}
        <div
          style={{
            background: "rgba(0, 29, 129, 0.5)",
            border: "2px solid rgba(253, 250, 229, 0.2)",
            borderRadius: "12px",
            padding: "40px",
            marginBottom: "30px",
          }}
        >
          <h2
            style={{
              fontSize: "24px",
              fontWeight: 700,
              color: "#fdfae5",
              marginBottom: "12px",
            }}
          >
            Add to Balance
          </h2>
          <p
            style={{
              color: "rgba(253, 250, 229, 0.7)",
              marginBottom: "30px",
              fontSize: "15px",
            }}
          >
            Add funds to your balance. Each API operation deducts its actual cost.
          </p>
          <p
            style={{
              color: "rgba(253, 250, 229, 0.5)",
              marginBottom: "30px",
              fontSize: "13px",
            }}
          >
            Stripe applies a processing fee of{" "}
            <span style={{ color: "rgba(253, 250, 229, 0.7)", fontWeight: 600 }}>
              2.9% + $0.30
            </span>{" "}
            per transaction (e.g. $10.00 top-up → $10.59 charged). This fee
            goes entirely to Stripe.
          </p>

          {/* Preset Amount Buttons */}
          <div
            style={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
              marginBottom: "30px",
            }}
          >
            {[20, 50, 100, 200].map((amount) => (
              <button
                key={amount}
                onClick={() => handlePurchaseCredits(amount)}
                disabled={purchasingCredits}
                style={{
                  padding: "16px 32px",
                  background: purchasingCredits
                    ? "rgba(253, 250, 229, 0.2)"
                    : "#fdfae5",
                  color: purchasingCredits
                    ? "rgba(253, 250, 229, 0.5)"
                    : "#001765",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "18px",
                  fontWeight: 700,
                  cursor: purchasingCredits ? "not-allowed" : "pointer",
                  transition: "all 0.2s",
                  minWidth: "120px",
                }}
                onMouseEnter={(e) => {
                  if (!purchasingCredits) {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 12px rgba(253, 250, 229, 0.3)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                ${amount}
              </button>
            ))}
          </div>

          {/* Custom Amount Input */}
          <div
            style={{
              display: "flex",
              gap: "12px",
              alignItems: "stretch",
              maxWidth: "500px",
            }}
          >
            <div style={{ flex: 1 }}>
              <input
                type="number"
                min="1"
                max="1000"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                placeholder="Custom amount ($1 - $1000)"
                disabled={purchasingCredits}
                style={{
                  width: "100%",
                  padding: "16px",
                  background: "rgba(253, 250, 229, 0.1)",
                  border: "2px solid rgba(253, 250, 229, 0.3)",
                  borderRadius: "8px",
                  color: "#fdfae5",
                  fontSize: "16px",
                  fontWeight: 500,
                  outline: "none",
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = "#fdfae5";
                  e.currentTarget.style.background =
                    "rgba(253, 250, 229, 0.15)";
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor =
                    "rgba(253, 250, 229, 0.3)";
                  e.currentTarget.style.background = "rgba(253, 250, 229, 0.1)";
                }}
              />
            </div>
            <button
              onClick={handleCustomAmountPurchase}
              disabled={purchasingCredits || !customAmount}
              style={{
                padding: "16px 32px",
                background:
                  purchasingCredits || !customAmount
                    ? "rgba(253, 250, 229, 0.2)"
                    : "#fdfae5",
                color:
                  purchasingCredits || !customAmount
                    ? "rgba(253, 250, 229, 0.5)"
                    : "#001765",
                border: "none",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: 700,
                cursor:
                  purchasingCredits || !customAmount
                    ? "not-allowed"
                    : "pointer",
                transition: "all 0.2s",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => {
                if (!purchasingCredits && customAmount) {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(253, 250, 229, 0.3)";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              {purchasingCredits ? "Processing..." : "Purchase"}
            </button>
          </div>
        </div>

        {/* Pricing Breakdown (collapsible) */}
        <div
          style={{
            background: "rgba(0, 29, 129, 0.5)",
            border: "2px solid rgba(253, 250, 229, 0.2)",
            borderRadius: "12px",
            padding: "24px 40px",
            marginBottom: "30px",
          }}
        >
          <button
            onClick={() => setShowPricing(!showPricing)}
            style={{
              background: "none",
              border: "none",
              color: "#fdfae5",
              fontSize: "18px",
              fontWeight: 700,
              cursor: "pointer",
              padding: 0,
              display: "flex",
              alignItems: "center",
              gap: "10px",
              width: "100%",
            }}
          >
            <span style={{ fontSize: "14px" }}>
              {showPricing ? "\u25BC" : "\u25B6"}
            </span>
            Pricing
            <span
              style={{
                fontSize: "14px",
                fontWeight: 400,
                color: "rgba(253, 250, 229, 0.5)",
              }}
            >
              — API costs passed through at cost, no markup
            </span>
          </button>

          {showPricing && (
            <div style={{ marginTop: "20px" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "15px",
                }}
              >
                <thead>
                  <tr
                    style={{
                      borderBottom: "2px solid rgba(253, 250, 229, 0.2)",
                    }}
                  >
                    <th
                      style={{
                        textAlign: "left",
                        padding: "12px 16px",
                        color: "rgba(253, 250, 229, 0.6)",
                        fontWeight: 600,
                        fontSize: "13px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Operation
                    </th>
                    <th
                      style={{
                        textAlign: "right",
                        padding: "12px 16px",
                        color: "rgba(253, 250, 229, 0.6)",
                        fontWeight: 600,
                        fontSize: "13px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Cost
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "12px 16px",
                        color: "rgba(253, 250, 229, 0.6)",
                        fontWeight: 600,
                        fontSize: "13px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Provider
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Image generation", "$0.03/image", "Stability AI Core"],
                    ["Image generation", "$0.03/image", "Flux (fal.ai)"],
                    ["Chat completion", "~$0.01/call", "OpenRouter"],
                    ["Object segmentation", "$0.005/request", "SAM-3 (fal.ai)"],
                    ["3D reconstruction", "$0.02/object", "SAM-3D (fal.ai)"],
                    ["Image \u2192 3D mesh", "$0.10/mesh", "Stable Fast 3D"],
                    ["Image \u2192 3D mesh", "~$0.05/mesh", "Trellis (Replicate)"],
                    ["Retexture", "~$0.10/run", "MVAdapter (Replicate)"],
                  ].map(([op, cost, provider], i) => (
                    <tr
                      key={i}
                      style={{
                        borderBottom: "1px solid rgba(253, 250, 229, 0.1)",
                      }}
                    >
                      <td
                        style={{
                          padding: "14px 16px",
                          color: "#fdfae5",
                          fontWeight: 500,
                        }}
                      >
                        {op}
                      </td>
                      <td
                        style={{
                          padding: "14px 16px",
                          textAlign: "right",
                          color: "#fdfae5",
                          fontWeight: 600,
                        }}
                      >
                        {cost}
                      </td>
                      <td
                        style={{
                          padding: "14px 16px",
                          color: "rgba(253, 250, 229, 0.6)",
                        }}
                      >
                        {provider}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* API Usage Table */}
        <div
          style={{
            background: "rgba(0, 29, 129, 0.5)",
            border: "2px solid rgba(253, 250, 229, 0.2)",
            borderRadius: "12px",
            padding: "40px",
          }}
        >
          <h2
            style={{
              fontSize: "24px",
              fontWeight: 700,
              color: "#fdfae5",
              marginBottom: "30px",
            }}
          >
            API Usage History
          </h2>

          {apiCalls.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "60px 20px",
                color: "rgba(253, 250, 229, 0.6)",
              }}
            >
              <div
                style={{ fontSize: "48px", marginBottom: "16px", opacity: 0.5 }}
              >
                📊
              </div>
              <p
                style={{
                  fontSize: "18px",
                  fontWeight: 500,
                  marginBottom: "8px",
                  color: "rgba(253, 250, 229, 0.8)",
                }}
              >
                No API calls yet
              </p>
              <p style={{ fontSize: "15px" }}>
                Start using the API to see your usage history here
              </p>
            </div>
          ) : (
            <>
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "15px",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        borderBottom: "2px solid rgba(253, 250, 229, 0.2)",
                      }}
                    >
                      <th
                        style={{
                          textAlign: "left",
                          padding: "16px",
                          color: "rgba(253, 250, 229, 0.6)",
                          fontWeight: 600,
                          fontSize: "13px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Endpoint
                      </th>
                      <th
                        style={{
                          textAlign: "center",
                          padding: "16px",
                          color: "rgba(253, 250, 229, 0.6)",
                          fontWeight: 600,
                          fontSize: "13px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Status
                      </th>
                      <th
                        style={{
                          textAlign: "right",
                          padding: "16px",
                          color: "rgba(253, 250, 229, 0.6)",
                          fontWeight: 600,
                          fontSize: "13px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Cost
                      </th>
                      <th
                        style={{
                          textAlign: "right",
                          padding: "16px",
                          color: "rgba(253, 250, 229, 0.6)",
                          fontWeight: 600,
                          fontSize: "13px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Time
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiCalls.map((call) => (
                      <tr
                        key={call.id}
                        style={{
                          borderBottom: "1px solid rgba(253, 250, 229, 0.1)",
                        }}
                      >
                        <td style={{ padding: "20px 16px" }}>
                          <code
                            style={{
                              fontSize: "14px",
                              fontWeight: 500,
                              color: "#fdfae5",
                              background: "rgba(253, 250, 229, 0.1)",
                              padding: "4px 8px",
                              borderRadius: "4px",
                            }}
                          >
                            {call.endpoint}
                          </code>
                          {call.error_message && (
                            <div
                              style={{
                                fontSize: "13px",
                                color: "#dc3545",
                                marginTop: "8px",
                              }}
                            >
                              {call.error_message}
                            </div>
                          )}
                        </td>
                        <td
                          style={{ padding: "20px 16px", textAlign: "center" }}
                        >
                          <span
                            style={{
                              padding: "6px 14px",
                              borderRadius: "16px",
                              fontSize: "13px",
                              fontWeight: 600,
                              background:
                                call.status === "success"
                                  ? "#28a745"
                                  : "#dc3545",
                              color: "white",
                            }}
                          >
                            {call.status.toUpperCase()}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: "20px 16px",
                            textAlign: "right",
                            color: "#fdfae5",
                            fontWeight: 600,
                          }}
                        >
                          ${call.credits_consumed.toFixed(4)}
                        </td>
                        <td
                          style={{
                            padding: "20px 16px",
                            textAlign: "right",
                            color: "rgba(253, 250, 229, 0.6)",
                            fontSize: "14px",
                          }}
                        >
                          {formatDateTime(call.called_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pagination && pagination.has_more && (
                <div style={{ marginTop: "30px", textAlign: "center" }}>
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    style={{
                      padding: "12px 32px",
                      background: loadingMore
                        ? "rgba(253, 250, 229, 0.2)"
                        : "#fdfae5",
                      color: loadingMore
                        ? "rgba(253, 250, 229, 0.5)"
                        : "#001765",
                      border: "none",
                      borderRadius: "6px",
                      fontSize: "15px",
                      fontWeight: 600,
                      cursor: loadingMore ? "not-allowed" : "pointer",
                    }}
                  >
                    {loadingMore ? "Loading..." : "Load More"}
                  </button>
                  <p
                    style={{
                      marginTop: "12px",
                      fontSize: "14px",
                      color: "rgba(253, 250, 229, 0.6)",
                    }}
                  >
                    Showing {apiCalls.length} of {pagination.total} calls
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
